import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAppSelector } from "app/hooks";
import { selectLastViewedChecklist } from "../orderSlice";
import { Checkbox, Link } from "@mui/material";
import { Checklist } from "models/Checklist";
import DataTable from "components/DataTable";
import { House } from "@mui/icons-material";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { useOrderContext } from "context/OrderContext";
import { useExcludeChecklistMutation } from "app/api/orders";
import ChecklistStatusIndicators from "./ChecklistStatusIndicators";

type Props = {
  checklists: Checklist[];
};

const ChecklistsTable: React.FC<Props> = ({ checklists }) => {
  const order = useOrderContext();
  const hasPreviousChecklist = useAppSelector(selectLastViewedChecklist);
  const { user } = useAuthContext();
  const [excludeChecklist] = useExcludeChecklistMutation();

  const userCanExcludeChecklists =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.USER_ADMIN ||
    user?.role === UserRole.SALES;

  // Scroll to last visited checklist (when everything is loaded)
  useEffect(() => {
    if (!hasPreviousChecklist) return;

    const element = document.getElementById(`${hasPreviousChecklist}`);
    element?.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "nearest",
    });
  }, [hasPreviousChecklist]);

  const toggleChecklistExcluded = (checklist: Checklist) => {
    excludeChecklist({
      orderId: order.ordernr,
      checklistId: checklist.id,
      excludedFromReport: !checklist.excludedFromReport,
    });
  };

  return (
    <DataTable
      striped
      noBottomBorder
      data={checklists}
      getKey={(checklist) => checklist.id}
      rowStyles={(checklist) => ({
        opacity: checklist.excludedFromReport ? 0.33 : 1,
      })}
      columns={[
        {
          key: "roomName",
          label: "Rum",
          renderCell: (checklist) => (
            <Link
              component={RouterLink}
              to={`/orders/view/${order.ordernr}/checklist/${checklist.id}`}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <House
                color={
                  checklist.functionCheck && checklist.documentationDone ? "success" : "disabled"
                }
              />
              {checklist.roomName}
            </Link>
          ),
        },
        {
          key: "creator",
          label: "Skapad av",
          renderCell: (checklist) => checklist.creator.userName,
          sortBy: (checklist) => checklist.creator.userName,
        },
        {
          key: "updatedAt",
          label: "Ändrad",
        },
        {
          key: "status",
          label: "Status",
          dense: true,
          renderCell: (checklist) => <ChecklistStatusIndicators checklist={checklist} />,
          sortBy: (checklist) => {
            let statusValue = 0;
            if (checklist.documentationDone) statusValue += 1;
            if (checklist.functionCheck) statusValue += 2;
            if (checklist.approval) statusValue += 4;
            return statusValue;
          },
        },
        {
          key: "excludedFromReport",
          label: "Inkluderad i slutrapport?",
          align: "right",
          dense: true,
          renderCell: (checklist) => (
            <Checkbox
              disabled={!userCanExcludeChecklists}
              checked={!checklist.excludedFromReport}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleChecklistExcluded(checklist)}
              sx={{ p: 0 }}
            />
          ),
        },
      ]}
    />
  );
};

export default ChecklistsTable;
